
import { useState, useEffect } from 'react';

interface CookieCategory {
  id: string;
  name: string;
  description: string;
  required: boolean;
  enabled: boolean;
}

interface CookieSettingsProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (settings: Record<string, boolean>) => void;
}

const CookieSettings = ({ isOpen, onClose, onSave }: CookieSettingsProps) => {
  const [categories, setCategories] = useState<CookieCategory[]>([
    {
      id: 'necessary',
      name: 'Notwendige Cookies',
      description: 'Diese Cookies sind für das Funktionieren der Website erforderlich und können nicht deaktiviert werden.',
      required: true,
      enabled: true,
    },
    {
      id: 'analytics',
      name: 'Analyse-Cookies',
      description: 'Diese Cookies helfen uns zu verstehen, wie Besucher mit unserer Website interagieren, indem sie Informationen anonym sammeln und melden.',
      required: false,
      enabled: false,
    },
    {
      id: 'marketing',
      name: 'Marketing-Cookies',
      description: 'Diese Cookies werden verwendet, um Ihnen relevante Werbung und Inhalte zu zeigen.',
      required: false,
      enabled: false,
    },
    {
      id: 'functional',
      name: 'Funktionale Cookies',
      description: 'Diese Cookies ermöglichen erweiterte Funktionalitäten und Personalisierung.',
      required: false,
      enabled: false,
    },
  ]);

  useEffect(() => {
    if (isOpen) {
      const savedSettings = localStorage.getItem('cookie-settings');
      if (savedSettings) {
        const settings = JSON.parse(savedSettings);
        setCategories(prev => prev.map(cat => ({
          ...cat,
          enabled: cat.required || settings[cat.id] || false
        })));
      }
    }
  }, [isOpen]);

  const handleToggle = (categoryId: string) => {
    setCategories(prev => prev.map(cat => 
      cat.id === categoryId && !cat.required 
        ? { ...cat, enabled: !cat.enabled }
        : cat
    ));
  };

  const handleSave = () => {
    const settings = categories.reduce((acc, cat) => {
      acc[cat.id] = cat.enabled;
      return acc;
    }, {} as Record<string, boolean>);

    localStorage.setItem('cookie-settings', JSON.stringify(settings));
    localStorage.setItem('cookie-consent', 'custom');
    localStorage.setItem('cookie-consent-date', new Date().toISOString());
    
    onSave(settings);
    onClose();
  };

  const handleAcceptAll = () => {
    const allEnabled = categories.reduce((acc, cat) => {
      acc[cat.id] = true;
      return acc;
    }, {} as Record<string, boolean>);

    localStorage.setItem('cookie-settings', JSON.stringify(allEnabled));
    localStorage.setItem('cookie-consent', 'all');
    localStorage.setItem('cookie-consent-date', new Date().toISOString());
    
    onSave(allEnabled);
    onClose();
  };

  const handleRejectAll = () => {
    const necessaryOnly = categories.reduce((acc, cat) => {
      acc[cat.id] = cat.required;
      return acc;
    }, {} as Record<string, boolean>);

    localStorage.setItem('cookie-settings', JSON.stringify(necessaryOnly));
    localStorage.setItem('cookie-consent', 'necessary');
    localStorage.setItem('cookie-consent-date', new Date().toISOString());
    
    onSave(necessaryOnly);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900">Cookie-Einstellungen</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <i className="ri-close-line text-2xl"></i>
            </button>
          </div>

          <div className="mb-6">
            <p className="text-gray-600">
              Wir verwenden Cookies, um Ihnen die bestmögliche Nutzererfahrung zu bieten. 
              Sie können auswählen, welche Kategorien von Cookies Sie zulassen möchten. 
              Beachten Sie, dass das Blockieren einiger Arten von Cookies Ihre Erfahrung auf unserer Website beeinträchtigen kann.
            </p>
          </div>

          <div className="space-y-6">
            {categories.map((category) => (
              <div key={category.id} className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-lg font-semibold text-gray-900">{category.name}</h3>
                  <div className="flex items-center">
                    {category.required ? (
                      <span className="text-sm text-gray-500 mr-3">Immer aktiv</span>
                    ) : (
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={category.enabled}
                          onChange={() => handleToggle(category.id)}
                          className="sr-only"
                        />
                        <div className={`w-11 h-6 rounded-full transition-colors ${
                          category.enabled ? 'bg-blue-600' : 'bg-gray-300'
                        }`}>
                          <div className={`w-5 h-5 bg-white rounded-full shadow transform transition-transform ${
                            category.enabled ? 'translate-x-5' : 'translate-x-0'
                          } mt-0.5 ml-0.5`}></div>
                        </div>
                      </label>
                    )}
                  </div>
                </div>
                <p className="text-sm text-gray-600">{category.description}</p>
                
                {category.id === 'analytics' && (
                  <div className="mt-3 text-sm text-gray-500">
                    <strong>Verwendete Tools:</strong> Google Analytics, Google Tag Manager
                  </div>
                )}
                
                {category.id === 'functional' && (
                  <div className="mt-3 text-sm text-gray-500">
                    <strong>Verwendete Tools:</strong> Font Awesome, Remix Icons, Google Fonts
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="flex flex-col sm:flex-row gap-3 mt-8">
            <button
              onClick={handleRejectAll}
              className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 transition-colors whitespace-nowrap"
            >
              Alle ablehnen
            </button>
            <button
              onClick={handleSave}
              className="flex-1 px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 transition-colors whitespace-nowrap"
            >
              Auswahl speichern
            </button>
            <button
              onClick={handleAcceptAll}
              className="flex-1 px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700 transition-colors whitespace-nowrap"
            >
              Alle akzeptieren
            </button>
          </div>

          <div className="mt-6 pt-4 border-t border-gray-200">
            <p className="text-xs text-gray-500">
              Weitere Informationen finden Sie in unserer{' '}
              <a href="/datenschutz" className="text-blue-600 hover:text-blue-800 underline">
                Datenschutzerklärung
              </a>.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookieSettings;